import {
  IconButton,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Icon,
  Box,
  Divider,
  Stack,
} from "@chakra-ui/react";
import { FormikProps, useFormik } from "formik";
import { isEqual, isObject, transform } from "lodash";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  WarehouseTypeDetails,
  YupValidation,
} from "../../../../common/types/common";
import {
  Connection,
  ConnectionConfig,
  ConnectionCreateConfig,
  ConnectionUpdateConfig,
} from "../../../../common/types/connection";
import { createConnection, updateConnection } from "../connectionSlice";
import { object } from "yup";
import {
  requiredString,
  requiredNumber,
} from "../../../../common/helper/validationHelper";
import InputFieldWithError from "../../../../components/InputFieldWithError";
import { PasswordField } from "../../../../components/PasswordField";
import IButton, { BUTTON } from "../../../../components/IButton";
import ITitle from "../../../../components/ITitle";

function ConnectionFormControl({
  label,
  placeholder,
  type = "text",
  form,
  id,
}: {
  label?: string;
  placeholder?: string;
  type?: string;
  form: FormikProps<ConnectionConfig>;
  id: keyof ConnectionConfig;
}) {
  return (
    <>
      {label && (
        <InputFieldWithError
          labelText={label}
          placeholder={placeholder || ""}
          type={type}
          errorMsg={form.errors[id] ? `${label} ${form.errors[id]}` : ""}
          touched={!!form.touched[id]}
          name={id}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values[id]}
          formLabelProps={{ fontSize: "sm" }}
        />
      )}
    </>
  );
}

export default function ConnectionDetailsForm({
  connectionDetails,
  serviceDetails,
  loading,
  closeModal,
  goToPrevStep,
}: {
  connectionDetails?: Connection;
  serviceDetails: WarehouseTypeDetails;
  loading: boolean;
  closeModal: () => void;
  goToPrevStep?: () => void;
}) {
  const dispatch = useDispatch();

  let label = "",
    hostName = "",
    port: Connection["port"] = undefined,
    databaseName = "",
    userName = "";

  if (connectionDetails) {
    ({
      label,
      host_name: hostName,
      port,
      database_name: databaseName,
      user_name: userName,
    } = connectionDetails);
  }

  function findChangedProperties(object: any, base: any) {
    return transform(object, (result: any, value, key) => {
      if (!isEqual(value, base[key])) {
        result[key] =
          isObject(value) && isObject(base[key])
            ? findChangedProperties(value, base[key])
            : value;
      }
    });
  }

  // connection name and hostname are required for all services
  let connectionDetailsShape: YupValidation = {
    label: requiredString(""),
    host_name: requiredString(""),
  };

  //rest of the fields, change according to the service selected
  if (serviceDetails.fields.database_name) {
    connectionDetailsShape.database_name = requiredString("");
  }

  if (serviceDetails.fields.user_name) {
    connectionDetailsShape.user_name = requiredString("");
  }

  if (serviceDetails?.fields.port) {
    connectionDetailsShape.port = requiredNumber("");
  }

  if (serviceDetails?.fields.password && !connectionDetails) {
    connectionDetailsShape.password = requiredString("Password");
  }

  const connectionDetailsSchema = object().shape(connectionDetailsShape);

  const connectionDetailsForm = useFormik<ConnectionConfig>({
    initialValues: {
      label: label || "",
      host_name: hostName || "",
      port: port || undefined,
      database_name: databaseName || undefined,
      user_name: userName || undefined,
      password: undefined,
    },

    onSubmit: async (values) => {
      if (connectionDetails) {
        const changedValues = findChangedProperties(values, {
          label,
          host_name: hostName,
          port,
          database_name: databaseName,
          user_name: userName,
        });
        if (!changedValues.password) {
          delete changedValues.password;
        }
        const dwCreds: ConnectionUpdateConfig = {
          ...changedValues,
          ...{ connection_id: connectionDetails.id },
        };
        dispatch(updateConnection(dwCreds));
      } else {
        const dwCreds: ConnectionCreateConfig = {
          ...values,
          ...{ typ: serviceDetails.client_id },
        };
        dispatch(createConnection(dwCreds));
      }
    },

    validationSchema: connectionDetailsSchema,
  });

  return (
    <>
      <ModalHeader
        pl={goToPrevStep ? 2 : undefined}
        color="blue.700"
        fontSize="sm"
      >
        {goToPrevStep && (
          <IconButton
            aria-label="Go Back"
            variant="ghost"
            size={"sm"}
            name="modal-return-button"
            icon={<Icon as={FaArrowLeft} />}
            mr={2}
            onClick={goToPrevStep}
          />
        )}
        {serviceDetails?.name}
      </ModalHeader>
      <ModalCloseButton />
      <form onSubmit={connectionDetailsForm.handleSubmit}>
        <ModalBody px={12}>
          <ConnectionFormControl
            id="label"
            label="Connection Name"
            form={connectionDetailsForm}
          />
          <Divider my={5} />
          <Box>
            <FormLabel fontSize={"xs"} mb="2">
              <ITitle title="Connection details" />
            </FormLabel>
            <Stack spacing={3}>
              <ConnectionFormControl
                id="host_name"
                label={serviceDetails.fields.host_name.label}
                placeholder={serviceDetails.fields.host_name.placeholder}
                form={connectionDetailsForm}
              />
              <ConnectionFormControl
                id="port"
                type="number"
                label={serviceDetails.fields.port?.label}
                placeholder={serviceDetails.fields.port?.placeholder}
                form={connectionDetailsForm}
              />
              <ConnectionFormControl
                id="database_name"
                label={serviceDetails.fields.database_name?.label}
                placeholder={serviceDetails.fields.database_name?.placeholder}
                form={connectionDetailsForm}
              />
              <ConnectionFormControl
                id="user_name"
                label={serviceDetails.fields.user_name?.label}
                placeholder={serviceDetails.fields.user_name?.placeholder}
                form={connectionDetailsForm}
              />
              {serviceDetails?.fields.password && (
                <FormControl
                  id="password"
                  isInvalid={
                    !!connectionDetailsForm.errors.password &&
                    connectionDetailsForm.touched.password
                  }
                >
                  <FormLabel fontSize="sm">Password</FormLabel>
                  <PasswordField
                    name="password"
                    onChange={connectionDetailsForm.handleChange}
                    onBlur={connectionDetailsForm.handleBlur}
                    value={connectionDetailsForm.values.password}
                    fontSize={12}
                  />
                  <FormErrorMessage fontSize="12px">
                    {connectionDetailsForm.errors.password}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Stack>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="flex-start" px={12} py={6}>
          <IButton
            variant="ghost"
            onClick={closeModal}
            name="cancel-button"
            mr={3}
          >
            Cancel
          </IButton>
          <IButton
            variant={BUTTON.PRIMARY}
            type="submit"
            isLoading={loading}
            isDisabled={loading || !connectionDetailsForm.isValid}
            loadingText="Saving Connection"
            name="save-button"
            px={5}
            leftIcon={<Icon as={FaSave} />}
          >
            Save Connection
          </IButton>
        </ModalFooter>
      </form>
    </>
  );
}
